import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * MediCare+ status chip.
 *
 * Tinted fills rather than solid ones, including for errors: a page of solid
 * red chips is how alarm fatigue starts, and a chip is a label, not an alert.
 * The uppercase 12px bold treatment is what marks these as *status* rather
 * than ordinary text.
 *
 * Every variant pairs its colour with a word, and callers are expected to keep
 * it that way — colour alone is not a status.
 */
const badgeVariants = cva(
  [
    "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap",
    "rounded-sm border px-3 py-1",
    "text-xs font-bold tracking-wide uppercase",
    "transition-colors focus-ring",
    "[&>svg]:pointer-events-none [&>svg]:size-3",
  ],
  {
    defaultVariants: {
      variant: "default",
    },
    variants: {
      variant: {
        info: "border-info-border bg-info-surface text-info",
        // The neutral chip. Not in the spec's list, but a status set with no
        // resting state forces every chip to imply success or a problem.
        secondary: "border-border bg-muted text-muted-foreground",
        success: "border-success-border bg-success-surface text-success",
        warning: "border-warning-border bg-warning-surface text-warning",
        destructive: "border-destructive-border bg-destructive-surface text-destructive",
        outline:
          "border-input bg-transparent text-foreground [a&]:hover:bg-accent",
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary-hover",
      },
    },
  }
)

function Badge({
  variant,
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
